import _ from 'lodash';
import React from 'react';
import ReactTooltip from 'react-tooltip';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { makeStyles, IconButton } from '@material-ui/core';
import Combokeys from 'combokeys';
import { BoundingBox, getBoxStyle, BoxRender } from './ocr';
import FocusBoxTooltip from './FocusBoxTooltip';
import { useAttachmentZIndexStyle } from './document';
import EditableBoundingBox from './EditableBoundingBox';
import { BoundingBox as BoundingBoxFieldType } from '@scale/shared/types/response/textcollection';
import { MOBILE_THRESHOLD } from '@scale/frontend-lib/util';
import ImageAdjustmentButton from './ImageAdjustmentButton';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { fromS3Url } from '@scale/shared/utils';
import { useZoomHook, ZoomHookProps } from './useZoomHook';

export const GUTTER = 24;

interface PanZoomImageProps {
  src: string;
  disabled?: boolean;
  boxes: BoundingBox[];
  size: { width: number; height: number };
  editBoundingBoxMode?: boolean;
  boundingBoxField?: BoundingBoxFieldType;
  updateBoundingBoxField?: (bbox: BoundingBoxFieldType) => void;
  caption?: string;
}

export default function PanZoomImage(props: PanZoomImageProps) {
  const canvas = React.useRef<HTMLDivElement | null>(null);
  const box = React.useRef<HTMLDivElement | null>(null);
  const [footerVisible, setFooterVisible] = React.useState<boolean>(false);
  const footerHideTimer = React.useRef<number | null>(null);
  const zIndexStyle = useAttachmentZIndexStyle(1);
  const [rotate, setRotate] = React.useState(0);
  const [brightness, setBrightness] = React.useState(1);
  const [contrast, setContrast] = React.useState(1);
  const [url, setUrl] = React.useState(props.src);
  const styles = usePanZoomImageStyles({
    footerVisible,
  });
  const zoomHookProps: ZoomHookProps = {
    size: { width: props.size.width, height: props.size.height },
    canvas,
  };
  const zoomHook = useZoomHook(zoomHookProps);
  const {
    setImageSize,
    imgWidth,
    imgHeight,
    newWidth,
    newHeight,
    offsetX,
    offsetY,
    zoom,
    onZoomIncrease,
    onZoomDecrease,
    onDoubleClick,
    formatPercent,
    Minus,
    Plus,
    zoomLevels,
  } = zoomHook;

  const CU_SENSITIVE_CONTENT_BUCKET = 'sensitive-cu-documents';

  React.useEffect(() => {
    const { Bucket: bucket } = fromS3Url(props.src);
    if (bucket === CU_SENSITIVE_CONTENT_BUCKET) {
      const signUrl = async () => {
        apiFetch(null, 'internal/sensitive-content-review/signTrustAndSafetyUrl', {
          method: 'GET',
          query: { url: props.src },
        })
          .then(result => {
            setUrl(result.url);
            return;
          })
          .catch(() => {
            setUrl(props.src);
          });
      };

      signUrl();
    } else {
      setUrl(props.src);
    }
  }, [props.src]);

  React.useEffect(() => {
    const image = new Image();
    image.onload = function () {
      setImageSize({
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    };
    image.src = url;
  }, [url]);

  React.useEffect(() => {
    if (box.current && typeof window !== 'undefined') {
      const handleMouseMove = () => {
        if (!_.isNil(footerHideTimer.current)) {
          window.clearTimeout(footerHideTimer.current);
        }
        setFooterVisible(true);
        const timer = window.setTimeout(() => {
          setFooterVisible(false);
        }, 2000);
        footerHideTimer.current = timer;
      };
      box.current.addEventListener('mousemove', handleMouseMove);
      return () => {
        box.current?.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [box.current]);

  const rotateCounterClockwise = () => {
    setRotate((rotate + 270) % 360);
  };

  React.useEffect(() => {
    if (props.disabled) {
      return;
    }
    const combokeys = new Combokeys(document.documentElement);

    // For combokeys to work correctly it can't reference any local
    // state where the variable reference will change between renders.
    combokeys.bind('plus', function () {
      onZoomIncrease();
    });
    combokeys.bind('minus', function () {
      onZoomDecrease();
    });
    return () => {
      combokeys.detach();
    };
  }, [onZoomDecrease, onZoomIncrease, props.disabled]);

  const highlightBoxes = props.boxes.filter(box => {
    return box.render === BoxRender.Highlight;
  });

  const possibleMatchBoxes = props.boxes.filter(box => {
    return box.render === BoxRender.PossibleMatch;
  });

  return (
    <div ref={box} className={styles.box}>
      <div ref={canvas} className={styles.viewport}>
        <div
          className={styles.canvas}
          onDoubleClick={onDoubleClick}
          style={{
            width: formatPercent(zoom),
            height: formatPercent(zoom),
            ...zIndexStyle,
          }}
        >
          <div
            className={styles.image}
            style={{
              position: 'absolute',
              top: offsetY,
              left: offsetX,
              width: newWidth,
              height: newHeight,
              backgroundImage: `url(${url})`,
              transform: `rotate(${rotate}deg)`,
              filter: `brightness(${brightness * 100}%) contrast(${contrast * 100}%)`,
              ...zIndexStyle,
            }}
          >
            <div className={styles.overlays}>
              {props.editBoundingBoxMode && props.updateBoundingBoxField && (
                <EditableBoundingBox
                  value={props.boundingBoxField}
                  onUpdate={props.updateBoundingBoxField}
                  naturalWidth={imgWidth}
                  naturalHeight={imgHeight}
                  offsetWidth={newWidth}
                  offsetHeight={newHeight}
                />
              )}
              {!props.disabled &&
                possibleMatchBoxes.map(bbox => {
                  // Force Tooltip to re-render if the bounding box position changes.
                  const key = `tooltip,${bbox.left.toFixed(6)},${bbox.top.toFixed(6)}`;
                  return <FocusBoxTooltip key={key} bbox={bbox} />;
                })}
              {highlightBoxes.map(bbox => {
                const key = `keyword,${bbox.left.toFixed(6)},${bbox.top.toFixed(6)}`;
                const boxStyle = getBoxStyle(bbox);
                return <div key={key} className={styles.highlight} style={boxStyle} />;
              })}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <div className={styles.toolbar} style={zIndexStyle}>
          <IconButton disabled={zoom <= zoomLevels[0]} onClick={onZoomDecrease}>
            <Minus />
          </IconButton>
          <div className={styles.zoom}>{formatPercent(zoom)}</div>
          <IconButton disabled={zoom >= zoomLevels[zoomLevels.length - 1]} onClick={onZoomIncrease}>
            <Plus />
          </IconButton>
        </div>
        <div className={styles.toolbar} style={zIndexStyle} data-tip data-for={'icon-rotate'}>
          <IconButton onClick={rotateCounterClockwise}>
            <Rotate90DegCCW />
          </IconButton>
          <ReactTooltip place={'right'} id={'icon-rotate'}>
            <div>
              <div>Rotate Image</div>
            </div>
          </ReactTooltip>
        </div>
        <div className={styles.toolbar} style={zIndexStyle}>
          <ImageAdjustmentButton
            value={brightness}
            onAdjust={setBrightness}
            iconName={'sun'}
            toolName={'Image Brightness'}
            adjustmentMultiplier={3}
          ></ImageAdjustmentButton>
        </div>
        <div className={styles.toolbar} style={zIndexStyle}>
          <ImageAdjustmentButton
            value={contrast}
            onAdjust={setContrast}
            iconName={'adjust'}
            toolName={'Image Contrast'}
            adjustmentMultiplier={4}
          ></ImageAdjustmentButton>
        </div>
      </div>
      {!!props.caption && (
        <div className={styles.topbar}>
          <div className={styles.caption} style={zIndexStyle}>
            {props.caption}
          </div>
        </div>
      )}
    </div>
  );
}

interface StyleProps {
  footerVisible: boolean;
}

const usePanZoomImageStyles = makeStyles({
  box: {
    width: '100%',
    height: `calc(100% - ${2 * GUTTER}px)`,
    position: 'relative',
    padding: GUTTER,
    [`@media (max-width: ${MOBILE_THRESHOLD})`]: {
      height: '400px',
    },
  },
  viewport: {
    position: 'relative',
    overflow: 'scroll',
    width: '100%',
    height: '100%',
  },
  canvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
  },
  image: {
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
  },
  overlays: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  footer: ({ footerVisible }: StyleProps) => ({
    position: 'absolute',
    width: '500px',
    left: '50%',
    marginLeft: '-104px',
    bottom: '12px',
    display: 'flex',
    flexDirection: 'row',
    opacity: footerVisible ? 1 : 0,
    transition: 'opacity 0.25s',
  }),
  topbar: {
    position: 'absolute',
    left: '40px',
    top: '12px',
    display: 'flex',
    flexDirection: 'row',
  },
  toolbar: {
    background: '#FFFFFF',
    boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.15)',
    borderRadius: '4px',
    textAlign: 'center',
    padding: '4px',
    margin: '4px',
  },
  zoom: {
    display: 'inline-block',
    color: '#808080',
    fontSize: '11px',
    lineHeight: '16px',
    letterSpacing: '0.1px',
    textAlign: 'center',
    width: '4em',
    userSelect: 'none',
  },
  highlight: {
    position: 'absolute',
    background: 'rgba(43,153,216,0.25)',
    borderBottom: '2px solid #4767D0',
    boxSizing: 'content-box',
  },
  caption: {
    background: '#FFFFFF',
    boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.15)',
    borderRadius: '4px',
    textAlign: 'center',
    paddingLeft: '8px',
    paddingRight: '8px',
    paddingTop: '4px',
    paddingBottom: '4px',
    margin: '4px',
  },
});
const Rotate90DegCCW = function () {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" height="16" viewBox="0 0 24 24" width="16">
      <path d="M0 0h24v24H0V0z" fill="none" />
      <path d="M7.34 6.41L.86 12.9l6.49 6.48 6.49-6.48-6.5-6.49zM3.69 12.9l3.66-3.66L11 12.9l-3.66 3.66-3.65-3.66zm15.67-6.26C17.61 4.88 15.3 4 13 4V.76L8.76 5 13 9.24V6c1.79 0 3.58.68 4.95 2.05 2.73 2.73 2.73 7.17 0 9.9C16.58 19.32 14.79 20 13 20c-.97 0-1.94-.21-2.84-.61l-1.49 1.49C10.02 21.62 11.51 22 13 22c2.3 0 4.61-.88 6.36-2.64 3.52-3.51 3.52-9.21 0-12.72z" />
    </svg>
  );
};
